import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getHealthRisk } from "@/lib/api";
import { Loader2 } from "lucide-react";

const NewAssessment = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    age: '',
    weight: '',
    height: '',
    systolic: '',
    diastolic: '',
    glucose: '',
    cholesterol: '',
  });

  const mutation = useMutation({ 
    mutationFn: getHealthRisk,
    onSuccess: (data) => {
      navigate("/assessment", { state: { assessmentResult: data } });
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const numericFormData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [key, Number(value)])
    );
    mutation.mutate(numericFormData);
  };

  return (
    <div>
      <Header />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">New Health Assessment</h1>
        <Card>
          <CardHeader>
            <CardTitle>Enter Your Health Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="age">Age</Label>
                <Input id="age" type="number" placeholder="e.g., 35" value={formData.age} onChange={handleChange} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weight">Weight (kg)</Label>
                <Input id="weight" type="number" placeholder="e.g., 75" value={formData.weight} onChange={handleChange} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="height">Height (cm)</Label>
                <Input id="height" type="number" placeholder="e.g., 180" value={formData.height} onChange={handleChange} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="systolic">Blood Pressure (Systolic)</Label>
                <Input id="systolic" type="number" placeholder="e.g., 120" value={formData.systolic} onChange={handleChange} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="diastolic">Blood Pressure (Diastolic)</Label>
                <Input id="diastolic" type="number" placeholder="e.g., 80" value={formData.diastolic} onChange={handleChange} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="glucose">Glucose Level (mg/dL)</Label>
                <Input id="glucose" type="number" placeholder="e.g., 90" value={formData.glucose} onChange={handleChange} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cholesterol">Cholesterol (mg/dL)</Label>
                <Input id="cholesterol" type="number" placeholder="e.g., 200" value={formData.cholesterol} onChange={handleChange} required />
              </div>
              <div className="md:col-span-2">
                <Button type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyzing...</>
                  ) : (
                    "Analyze My Health"
                  )}
                </Button>
                {mutation.isError && (
                  <p className="text-red-500 mt-2">An error occurred: {mutation.error.message}</p>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewAssessment;
